import React, { useState, useEffect } from "react";
import {
    Card,
    CardContent,
    Typography,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    MenuItem,
    Grid,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import config from "../config";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const DataGridComponent = () => {
    const [rows, setRows] = useState([]);
    const [search, setSearch] = useState("");
    const [pageSize, setPageSize] = useState(5);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState("Factory");
    const [boardrooms, setBoardrooms] = useState({ Factory: [], HeadOffice: [] });
    const [selectedLocation, setSelectedLocation] = useState("");
    const [openModal, setOpenModal] = useState(false);
    const [newBoardroom, setNewBoardroom] = useState({ name: "", status: true });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get(`${config.API_BASE_URL}/boardroom/getAvailableBoardrooms`);
                // console.log("API Response:", response.data);

                const data = response.data;
                setBoardrooms({
                    Factory: data.Factory || [],
                    HeadOffice: data.HeadOffice || [],
                });

                setRows(data.Factory || []);
                setLoading(false);
            } catch (err) {
                console.error("Error fetching data:", err);
                setError("Failed to load data.");
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleCategoryChange = (event) => {
        const category = event.target.value;
        setSelectedCategory(category);
        setRows(boardrooms[category] || []);
    };

    const handleSearch = (event) => {
        setSearch(event.target.value);
    };

    const handleLocationChange = (event) => {
        setSelectedLocation(event.target.value);
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setNewBoardroom((prev) => ({ ...prev, [name]: value }));
    };

    const handleOpenModal = () => setOpenModal(true);
    const handleCloseModal = () => {
        setOpenModal(false);
        setNewBoardroom({ name: "", status: true });
        setSelectedLocation("");
    };

    const handleSubmit = async () => {
        try {
            if (!selectedLocation) {
                toast.warning("Please select a location (Factory or HeadOffice)");
                return;
            }

            const response = await axios.post(
                `${config.API_BASE_URL}/boardroom/addBoardroom`,
                { ...newBoardroom, location: selectedLocation }
            );

            const updatedBoardrooms = {
                ...boardrooms,
                [selectedLocation]: [...(boardrooms[selectedLocation] || []), response.data],
            };

            setBoardrooms(updatedBoardrooms);

            // Show updated list of rows for selected location
            if (selectedCategory === selectedLocation) {
                setRows(updatedBoardrooms[selectedLocation]);
            }

            handleCloseModal();
            toast.success("Boardroom Added Successfully!!");
        } catch (err) {
            console.error("Error adding boardroom:", err);
        }
    };

    const filteredRows = rows.filter((row) =>
        row.name.toLowerCase().includes(search.toLowerCase())
    );

    const columns = [
        { field: "id", headerName: "#", width: 70 },
        { field: "name", headerName: "Name", flex: 1 },
        {
            field: "status",
            headerName: "Status",
            width: 120,
            renderCell: (params) => (
                <span
                    style={{
                        backgroundColor: params.value ? "green" : "red",
                        color: "white",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        textAlign: "center",
                        width: "80px",
                        display: "inline-block",
                    }}
                >
                    {params.value ? "Active" : "Non Active"}
                </span>
            ),
        }
    ];

    return (
        <Box component="main" sx={{ flexGrow: 1, padding: "10px", paddingLeft: "260px", paddingTop: "60px" }}>
            <Card sx={{ width: "100%", padding: "15px", borderRadius: "10px", borderTop: "4px solid blue" }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        List of Boardrooms
                    </Typography>
                    <hr />
                    {error && <Typography color="error">{error}</Typography>}

                    <Grid container alignItems="center" spacing={2}>
                        <Grid item>
                            <TextField
                                select
                                label="Category"
                                value={selectedCategory}
                                onChange={handleCategoryChange}
                                size="small"
                                style={{ width: "150px" }}
                            >
                                <MenuItem value="Factory">Factory</MenuItem>
                                <MenuItem value="HeadOffice">Head Office</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item>
                            <Button onClick={handleOpenModal} variant="contained" color="primary">
                                Add Boardroom
                            </Button>
                        </Grid>

                        <Grid item xs />
                        <Grid item>
                            <TextField
                                label="Search"
                                variant="outlined"
                                size="small"
                                value={search}
                                onChange={handleSearch}
                            />
                        </Grid>
                    </Grid>

                    <div style={{ marginTop: "10px" }} />

                    {loading ? (
                        <Typography>Loading...</Typography>
                    ) : (
                        <div style={{ width: "100%" }}>
                            <DataGrid
                                rows={filteredRows}
                                columns={columns}
                                disableSelectionOnClick
                                autoHeight
                                hideFooter
                                rowHeight={35}
                            />
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Modal */}
            <Dialog open={openModal} onClose={handleCloseModal}>
                <DialogTitle>Add New Boardroom</DialogTitle>
                <DialogContent>
                    <TextField
                        select
                        label="Select Location"
                        fullWidth
                        margin="normal"
                        value={selectedLocation}
                        onChange={handleLocationChange}
                    >
                        <MenuItem value="Factory">Factory</MenuItem>
                        <MenuItem value="HeadOffice">HeadOffice</MenuItem>
                    </TextField>
                    <TextField
                        label="Name"
                        fullWidth
                        margin="normal"
                        name="name"
                        value={newBoardroom.name}
                        onChange={handleInputChange}
                    />
                    <TextField
                        select
                        label="Status"
                        fullWidth
                        margin="normal"
                        name="status"
                        value={newBoardroom.status}
                        onChange={handleInputChange}
                    >
                        <MenuItem value={true}>Active</MenuItem>
                        <MenuItem value={false}>Non Active</MenuItem>
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal} color="secondary">Cancel</Button>
                    <Button onClick={handleSubmit} color="primary">Add</Button>
                </DialogActions>
            </Dialog>
            <ToastContainer />
        </Box>
    );
};

export default DataGridComponent;
